import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@/shared/api/queries";
import { useSessionStore } from "@/shared/session";
import { useToast } from "@/shared/components/Toast";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import {
  buildAppleAuthorizeUrl,
  buildGoogleAuthorizeUrl,
  readStoredReturnTo,
} from "@/shared/oauth";

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID;

/**
 * Sign-in screen (Hi-Fi 02 — desktop + mobile).
 *
 * Layout: split-card on hero gradient
 *   - LEFT (md+): tilted photo-collage hero with caption hook
 *     "Da khô như sa mạc?"
 *   - RIGHT: title "Mở app, 3 phút có clip." + descriptor + Google
 *     OAuth pill button + legal footer
 *
 * Auth (2026-05-20 v2): real Google OAuth via **redirect flow** (not popup).
 * Click → `window.location.href = …accounts.google.com/o/oauth2/v2/auth…` →
 * Google signs in → redirect back to `/oauth/callback#id_token=…`. OAuthCallback
 * parses the hash and exchanges the id_token with our Worker. Redirect avoids
 * the iOS Safari popup-becomes-new-tab quirk reported by Vincent.
 *
 * DEV_MOCK fallback button only renders when `VITE_GOOGLE_CLIENT_ID` is
 * unset (e.g. local dev without OAuth credentials) — keeps Maestro/web
 * smoke tests unblocked on a bare checkout.
 */
export function SignIn() {
  const navigate = useNavigate();
  const setSession = useSessionStore((s) => s.setSession);
  const toast = useToast();
  const { locale, t } = useTranslation();
  const google = useGoogleLogin();
  const [busy, setBusy] = useState(false);

  function handleGoogle() {
    if (!GOOGLE_CLIENT_ID) return;
    setBusy(true);
    // FL-2: forward the deep-link target saved by the auth guard so the user
    // lands back on the page they were trying to reach after sign-in.
    window.location.href = buildGoogleAuthorizeUrl({
      clientId: GOOGLE_CLIENT_ID,
      returnTo: readStoredReturnTo(),
    });
  }

  function handleApple() {
    if (!APPLE_CLIENT_ID) {
      toast.warning(t("signIn.appleNotConfigured"));
      return;
    }
    setBusy(true);
    // Apple uses the same redirect-flow pattern as Google so OAuthCallback
    // can handle both via the provider tag set in sessionStorage.
    window.location.href = buildAppleAuthorizeUrl({
      clientId: APPLE_CLIENT_ID,
      returnTo: readStoredReturnTo(),
    });
  }

  function handleEmailPlaceholder() {
    // Email/magic-link auth is Phase 2 per PRD §5. Surface a friendly
    // explainer so power-seller users who land here don't think it's
    // broken — direct them to OAuth instead.
    // FL-6: when Apple is not configured (button hidden) don't mention it.
    toast.info(
      APPLE_CLIENT_ID
        ? t("signIn.emailComingSoon")
        : t("signIn.emailComingSoonGoogleOnly"),
    );
  }

  // Local-only fallback (no real OAuth available). Sends a DEV_MOCK token —
  // the Worker will reject it on environments where DEV_MOCK_AUTH_ENABLED=false.
  async function handleDevMock() {
    setBusy(true);
    try {
      const res = await google.mutateAsync({
        // Domain must NOT be @isale.local — client.ts isStaleToken() purges
        // that domain (legacy DEV_MOCK guard from 2026-05-20), which would
        // discard this session on the next read and bounce back to sign-in.
        // @isale.app keeps the local DEV_MOCK session usable (local backend
        // honours DEV_MOCK when ENVIRONMENT=local; deployed dev/prod reject it).
        id_token: "DEV_MOCK_web-dev@isale.app",
        locale,
      });
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        userId: res.user.id,
        workspaceId: res.user.default_workspace_id,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(
        `Đăng nhập thất bại · ${err instanceof Error ? err.message : "—"}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-6">
      <div className="grid w-full max-w-5xl gap-8 md:grid-cols-2 md:gap-16">
        {/* LEFT — photo collage hero (desktop) */}
        <div className="hidden md:flex items-center justify-center">
          <PhotoCollage size="lg" />
        </div>

        {/* RIGHT — copy + buttons */}
        <div className="flex max-w-md flex-col justify-center">
          {/* iSale logo (per Hi-Fi 02 — logo top-left of right column) */}
          <div className="mb-6 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-fill text-white shadow-brand-fab">
              <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l2.39 7.36H22l-6.19 4.5L18.2 21 12 16.5 5.8 21l2.39-7.14L2 9.36h7.61L12 2z" />
              </svg>
            </div>
            <div className="leading-tight">
              <div className="text-base font-extrabold tracking-tight text-fg">iSale</div>
              <div className="text-xs font-bold uppercase tracking-wider text-fg-subtle">
                {t("signIn.tagline")}
              </div>
            </div>
          </div>

          {/* Compact collage on mobile so the headline + buttons stay
              above the fold on common phone viewports (375–414×730ish). */}
          <div className="md:hidden mb-6 flex justify-center">
            <PhotoCollage size="sm" />
          </div>

          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-fg md:text-4xl">
            {t("signIn.heroLine1")}
            <br />
            {t("signIn.heroLine2")}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-fg-subtle">
            {t("signIn.subCopy")}
          </p>

          {/* OAuth stack — Google primary, Apple secondary, Email tertiary
              (deferred). Reserve fixed heights so layout doesn't shift on
              click busy state. */}
          <div className="mt-6 flex flex-col gap-3">
            {GOOGLE_CLIENT_ID ? (
              <button
                type="button"
                onClick={handleGoogle}
                disabled={busy}
                className="flex h-14 w-full items-center justify-center gap-3 rounded-pill border border-default bg-elevated px-6 font-bold text-fg shadow-card transition hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
              >
                <GoogleGlyph />
                <span>{busy ? t("signIn.redirecting") : t("signIn.continueGoogle")}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDevMock}
                disabled={busy}
                className="flex h-14 w-full items-center justify-center gap-3 rounded-pill border border-default bg-elevated px-6 font-bold text-fg shadow-card transition hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
              >
                <GoogleGlyph />
                <span>{busy ? "Đang đăng nhập…" : "Tiếp tục (DEV_MOCK)"}</span>
              </button>
            )}

            {/* Apple Sign In — same redirect-flow pattern. Hidden on
                environments without VITE_APPLE_CLIENT_ID (e.g. local dev
                where Apple Service ID isn't configured); button still
                surfaces a friendly toast if user clicks before env wires. */}
            {APPLE_CLIENT_ID ? (
              // palette-ok: Apple-brand black button — Apple HIG mandates bg-black
              <button type="button" onClick={handleApple} disabled={busy} className="flex h-14 w-full items-center justify-center gap-3 rounded-pill bg-black px-6 font-bold text-white shadow-card transition hover:bg-black/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60">
                <AppleGlyph />
                <span>{t("signIn.continueApple")}</span>
              </button>
            ) : null}

            {/* "hoặc" divider before the email fallback — only renders if
                there's at least one OAuth button above. */}
            <div className="flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-border" />
              <div className="text-xs font-bold uppercase tracking-wider text-fg-subtle">
                {t("signIn.or")}
              </div>
              <div className="h-px flex-1 bg-border" />
            </div>

            <button
              type="button"
              onClick={handleEmailPlaceholder}
              disabled={busy}
              className="flex h-14 w-full items-center justify-center gap-3 rounded-pill border border-default bg-elevated px-6 font-bold text-fg-muted transition hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60"
            >
              <EmailGlyph />
              <span>{t("signIn.continueEmail")}</span>
            </button>
          </div>

          {/* Trust strip — replaces the "4.9/5 · 2,140 sellers" Hi-Fi
              social-proof block with HONEST pre-launch metrics. We show
              the verified "~3 min average clip time" benchmark + the
              "anti-hallucination guard" promise, no fake user counts. */}
          <div className="mt-6 flex items-center gap-4 text-xs text-fg-subtle">
            <div className="flex items-center gap-1.5">
              <span className="text-base">⚡</span>
              <span className="font-bold">{t("signIn.trustSpeed")}</span>
            </div>
            <div className="h-3 w-px bg-border" />
            <div className="flex items-center gap-1.5">
              <span className="text-base">🇻🇳</span>
              <span className="font-bold">{t("signIn.trustNative")}</span>
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-fg-subtle">
            {t("signIn.legalPrefix")}{" "}
            <a
              className="text-fg-muted underline hover:text-fg"
              href="https://isale.app/legal/terms"
              target="_blank"
              rel="noreferrer"
            >
              {t("signIn.legalTerms")}
            </a>{" "}
            {t("signIn.legalAnd")}{" "}
            <a
              className="text-fg-muted underline hover:text-fg"
              href="https://isale.app/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              {t("signIn.legalPrivacy")}
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function AppleGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="white">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function EmailGlyph() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

/**
 * Tilted photo-collage hero. `lg` (280px) on desktop split-card, `sm` (180px)
 * on mobile so it doesn't dominate the fold above the headline + button.
 */
function PhotoCollage({ size = "lg" }: { size?: "sm" | "lg" }) {
  const isSm = size === "sm";
  const dim = isSm ? 180 : 280;
  const cardH = isSm ? 130 : 210;
  const cardW = isSm ? 100 : 160;
  const centerH = isSm ? 145 : 230;
  const centerW = isSm ? 110 : 170;
  const emojiCls = isSm ? "text-4xl" : "text-6xl";
  return (
    <div
      className="relative mx-auto"
      style={{ height: dim, width: dim }}
    >
      {/* Left card */}
      <div
        className={`absolute left-0 top-2 flex items-center justify-center rounded-2xl ${emojiCls}`}
        style={{
          height: cardH,
          width: cardW,
          backgroundColor: "#818CF8", // palette-ok: decorative sign-in collage mock card
          transform: "rotate(-6deg)",
          boxShadow: "0 18px 30px -16px rgba(15,23,42,.35)", // palette-ok: fixed shadow color
        }}
      >
        🧴
      </div>
      {/* Right card */}
      <div
        className={`absolute right-0 top-0 flex items-center justify-center rounded-2xl ${emojiCls}`}
        style={{
          height: cardH,
          width: cardW,
          backgroundColor: "#F472B6", // palette-ok: decorative sign-in collage mock card
          transform: "rotate(7deg)",
          boxShadow: "0 18px 30px -16px rgba(15,23,42,.35)", // palette-ok: fixed shadow color
        }}
      >
        💄
      </div>
      {/* Center dark card with caption hook */}
      <div
        className="absolute bottom-0 left-1/2 flex -translate-x-1/2 flex-col items-center justify-end overflow-hidden rounded-2xl pb-2 text-center"
        style={{
          height: centerH,
          width: centerW,
          backgroundColor: "#1E1B4B", // palette-ok: decorative dark surface (sign-in collage card)
          boxShadow: "0 24px 40px -16px rgba(15,23,42,.45)", // palette-ok: fixed shadow color
        }}
      >
        <span className={`mb-auto mt-4 ${emojiCls}`}>🌿</span>
        <span className="px-2 text-3xs font-bold text-white md:text-xs">
          "Da khô như sa mạc?"
        </span>
        {/* palette-ok: play glyph sits on the fixed-dark collage card — stays a light circle in both themes */}
        <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white/95 text-primary-hover md:h-6 md:w-6">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24">
      <path
        fill="#4285F4" /* palette-ok: Google brand color (ADR-007 brand.external.* pending) */
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853" /* palette-ok: Google brand color (ADR-007 brand.external.* pending) */
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05" /* palette-ok: Google brand color (ADR-007 brand.external.* pending) */
        d="M5.84 14.1A6.5 6.5 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z"
      />
      <path
        fill="#EA4335" /* palette-ok: Google brand color (ADR-007 brand.external.* pending) */
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
